import { queryAll, showElement, hideElement, setExpanded } from './dom.js';

/**
 * Account Details view/edit toggling.
 *
 * Each block on the page exists twice in the markup: a view state
 * (`[data-account-view="key"]`) and an edit state
 * (`[data-account-edit="key"]`), paired by the shared key. Both are
 * rendered on load; the edit states ship hidden.
 *
 * Clicking Edit swaps the pair over and — modal-accordion style — closes
 * whichever other pair was open, so only one form is ever editable at a
 * time. Cancel swaps back and returns focus to the Edit button.
 *
 * Sections are not stable for the life of the page: saving a section posts
 * via htmx, which replaces that pair's wrapper (e.g. `#personal-info`) with
 * freshly rendered markup. So nodes are looked up on demand rather than
 * cached at init — cached references would point at detached elements and
 * the toggles would silently stop working after the first save.
 */
export function initAccountDetails() {
  const root = document.querySelector('.myaccount-account-details');
  if (!root) return;

  function sectionKeys() {
    return queryAll('[data-account-edit]', root).map(edit => edit.dataset.accountEdit);
  }

  function getPair(key) {
    const edit = root.querySelector(`[data-account-edit="${key}"]`);
    const view = root.querySelector(`[data-account-view="${key}"]`);
    const trigger = view?.querySelector('[data-action="edit-section"]');
    if (!edit || !view || !trigger) return null;
    return { view, edit, trigger };
  }

  const keys = sectionKeys();
  if (!keys.length) return;

  keys.forEach(key => {
    const pair = getPair(key);
    if (!pair) return;

    // Belt and braces: the markup already carries `.is-hidden` + `hidden`,
    // but re-assert it so the page can never load with a form open.
    hideElement(pair.edit);
    setExpanded(pair.trigger, false);
  });

  function close(key, { restoreFocus = false } = {}) {
    const pair = getPair(key);
    if (!pair) return;
    hideElement(pair.edit);
    showElement(pair.view);
    setExpanded(pair.trigger, false);
    if (restoreFocus) pair.trigger.focus();
  }

  function open(key) {
    const pair = getPair(key);
    if (!pair) return;

    // Modal behaviour — only one section open at a time.
    sectionKeys().forEach(otherKey => {
      if (otherKey !== key) close(otherKey);
    });

    hideElement(pair.view);
    showElement(pair.edit);
    setExpanded(pair.trigger, true);

    // Land the caret in the first field so keyboard users carry straight on
    // from the Edit button they just pressed.
    pair.edit.querySelector('.form-field__input')?.focus();
  }

  root.addEventListener('click', e => {
    const trigger = e.target.closest('[data-action="edit-section"]');
    if (trigger && root.contains(trigger)) {
      open(trigger.dataset.target);
      return;
    }

    const cancel = e.target.closest('[data-action="cancel-edit"]');
    if (cancel && root.contains(cancel)) {
      const section = cancel.closest('[data-account-edit]');
      if (section) close(section.dataset.accountEdit, { restoreFocus: true });
    }
  });

  // Escape closes the open section, matching the modal feel.
  root.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    const section = e.target.closest('[data-account-edit]');
    if (section && !section.hidden) {
      close(section.dataset.accountEdit, { restoreFocus: true });
    }
  });

}

export function initAccountBilling() {
  const root = document.getElementById("billing-info");
  if (!root)
    return;
  root.addEventListener("address-form:confirm", (e) => {
    const a = e.detail;
    const view = root.querySelector('[data-account-view="billing"]');
    const values = view.querySelectorAll(".myaccount-account-details__value");
    const nameSpan = values[0];
    if (nameSpan) {
      nameSpan.textContent = a.firstName + " " + a.lastName + ", "+ a.formattedOneLine;
    }
    const editLink = view.querySelector(".myaccount-account-details__edit");
    if (editLink) {
      editLink.dataset.savedId = a.id || "";
      editLink.textContent = a.id ? "Edit" : "Add";
    }
    const updated = view.querySelector('[data-account-updated="billing"]');
    if (updated) {
      updated.classList.remove("is-hidden");
      updated.hidden = false;
    }
  });
}